/**
 * Prepared vs Filed — the legacy's first Reconciliation sub-tab, rebuilt.
 *
 * The question is always the same and always specific: *does what I filed
 * match my books?* So the file is chosen per comparison rather than read from
 * a stored copy — the operator is holding a particular download and asking
 * about that one.
 *
 * **The convention is shown, not assumed.** `Diff = Prepared − Filed`;
 * positive means under-reported on the portal. Backwards, it inverts the
 * advice a practitioner acts on, so it sits on screen where they can check it.
 *
 * The exception lists carry the legacy's remedial verbs — *amend in a later
 * GSTR-1*, *add in a later GSTR-1*, *verify the books, or amend the return* —
 * because naming the remedy is most of the value, and the service supplies
 * them rather than this screen inventing one.
 */

import { Alert, Button } from '@fiveox/ui'
import { Scale, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ApiError,
  type FiledComparisonBody,
  type FiledHeads,
  type PeriodRecord,
  periods,
} from '../lib/api'
import {
  exceptionDocText,
  notComparedSentence,
  sectionLabel,
} from '../lib/filedCompare'
import { useClient } from './ClientLayout'

// All five. `cess` was dropped here while the service computed it, sent it
// and declared it — so a prepared-vs-filed cess mismatch was found and never
// shown, which is worse than not computing it at all.
const HEADS: Array<keyof FiledHeads> = ['taxable', 'igst', 'cgst', 'sgst', 'cess']

const HEAD_LABELS: Record<keyof FiledHeads, string> = {
  taxable: 'Taxable',
  igst: 'IGST',
  cgst: 'CGST',
  sgst: 'SGST',
  cess: 'Cess',
}

/** Indian digit grouping, and a sign the operator can read at a glance. */
function rupees(paise: number | undefined | null, signed = false): string {
  if (paise === null || paise === undefined) return '—'
  const value = paise / 100
  const text = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  if (!signed) return value < 0 ? `(${text})` : text
  if (value === 0) return '0.00'
  return value > 0 ? `+${text}` : `−${text}`
}

export function FiledCompare() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<FiledComparisonBody | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Scoped to the client in the URL (ND-C1). Before this, every screen
    // listed every client's periods together and the rail asked you to pick
    // one out of the pile — which is the shape of a wrong-client mistake.
    void periods
      .list({ client_id: client.id })
      .then((b) => {
        setRows(b.data)
        // NOT `cur ?? …`: the effect re-runs when the client changes, and
        // keeping the previous selection would leave another client's period
        // on screen. Right numbers, wrong client is the mistake this whole
        // slice exists to make impossible.
        setSelected(b.data[0]?.id ?? null)
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.'),
      )
  }, [client.id])

  const compare = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0]
      if (!file || !selected) return
      setBusy(true)
      setError(null)
      try {
        setBody((await periods.filedComparison(selected, file)).data)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That comparison could not be run.')
        setBody(null)
      } finally {
        setBusy(false)
      }
    },
    [selected],
  )

  const c = body?.comparison

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Scale size={20} /> GSTR-1: Prepared vs Filed
          </h1>
          <p className="gst-sub">
            What this app prepared from the books, against the return as actually filed
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button
            type="button"
            disabled={!selected || busy}
            onClick={() => fileInput.current?.click()}
            size="sm"
          >
            <Upload size={15} /> {busy ? 'Comparing…' : 'Choose filed return'}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              void compare(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <div className="flex flex-wrap gap-6">
        <aside className="w-72 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(row.id)
                      setBody(null)
                    }}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="truncate">{row.return_type}</span>
                    <span className="mt-0.5 block text-[11px]">{row.period}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!c ? (
            // The legacy's empty state explains the path well enough to keep.
            <div className="gst-empty">
              <p className="mb-2 font-medium">No filed GSTR-1 chosen</p>
              <p>
                Upload the return as <strong>actually filed on the GST portal</strong> to
                reconcile it against what this app prepared from the books.
              </p>
              <p className="mt-2">
                Take the filed-return <code>.json</code> from Returns Dashboard → GSTR-1 →
                Download Filed Return.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <p className="text-sm font-medium text-foreground">
                  {body.client_name} — {body.period.return_type} {body.period.period}
                </p>
                <p className="gst-meta">
                  Filed return for <span className="gst-mono">{body.filed_gstin}</span>
                </p>
              </div>

              <Alert tone={c.ok ? 'success' : 'warning'} className="mb-4">
                <p className="gst-panel-h">
                  Comparison
                  <span className={`gst-tag ${c.ok ? 'is-clear' : ''}`}>
                    {c.ok ? 'agrees' : 'differences found'}
                  </span>
                </p>
                <p className="text-sm">{c.convention}</p>
              </Alert>

              <div className="gst-table-wrap mb-6">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">GSTR-1 Table</th>
                      <th>Prepared</th>
                      <th>Filed</th>
                      {/* From the same list the cells are drawn from (N6): four were written
                          out by hand above five cells, so every column from Cess on sat
                          under the wrong heading and Status under nothing. */}
                      {HEADS.map((h) => (
                        <th key={h}>Diff {HEAD_LABELS[h]}</th>
                      ))}
                      <th className="is-text">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.sections.map((s, i) => (
                      <tr key={s.section} className={i % 2 === 1 ? 'is-a' : ''}>
                        <td className="is-text is-sec">
                          {sectionLabel(s.section)}
                        </td>
                        <td>{rupees(s.prepared.taxable)}</td>
                        <td>{rupees(s.filed.taxable)}</td>
                        {HEADS.map((h) => (
                          <td key={h}>{rupees(s.diff[h], true)}</td>
                        ))}
                        <td className="is-text">
                          <span className={`gst-chip ${s.ok ? 'is-ok' : 'is-stop'}`}>
                            {s.ok ? 'MATCHED' : 'DIFF'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ExceptionList
                title="Value mismatch — same document, different numbers"
                rows={c.mismatched}
              />
              <ExceptionList title="In books, not filed" rows={c.prepared_only} />
              <ExceptionList title="Filed, not in books" rows={c.filed_only} />
              {notComparedSentence(c.not_compared) && (
                <p className="gst-meta mb-6">{notComparedSentence(c.not_compared)}</p>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function ExceptionList({ title, rows }: { title: string; rows: Array<Record<string, unknown>> }) {
  if (rows.length === 0) return null
  return (
    <div className="mb-6">
      <h3 className="gst-sub mb-2">
        {title} — {rows.length}
      </h3>
      <div className="gst-table-wrap">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Section</th>
              <th className="is-text">Doc No</th>
              <th className="is-text">GSTIN</th>
              <th>Taxable</th>
              <th>Diff</th>
              <th className="is-text">What to do</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{sectionLabel(String(r.section ?? ''))}</td>
                <td className="is-text">{exceptionDocText(r)}</td>
                <td className="is-text">
                  <span className="gst-mono">{String(r.ctin ?? '—')}</span>
                </td>
                <td>
                  {rupees(
                    (r.taxable as number | undefined) ??
                      ((r.prepared as FiledHeads | undefined)?.taxable),
                  )}
                </td>
                <td>{rupees((r.diff as FiledHeads | undefined)?.taxable, true)}</td>
                <td className="is-text">{String(r.remedy ?? '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
